/**
 * Shared mutable state — mirrors the closure variables from the original IIFE.
 *
 * All modules import `state` and read/write properties directly.
 * This is intentionally mutable: the tracking script is a long-lived
 * singleton running in a browser page.
 */

import type { Config } from "./types.js";

export const state = {
  // --- Script configuration (set once during bootstrap) ---
  apiUrl: "",
  websiteId: "",

  // --- Runtime state ---
  config: null as Config | null,
  /** Config fetch finished (success or error) — command queue stops buffering */
  configSettled: false,
  initialized: false,
  /** Commands pushed on TheSocialTapQ before config settled */
  pendingCommands: [] as Array<{ m: string; a?: unknown[] }>,
  /** True while our own code calls gtag(); the dataLayer bridge skips those */
  selfDispatch: false,
  userData: {} as Record<string, string>,
  pendingUrls: [] as string[],

  // --- Pixel initialization tracking (prevents double-init) ---
  initializedPixels: {} as Record<string, boolean>,

  // --- Client-side deduplication ---
  firedEvents: {} as Record<string, number>,
  firstLoad: true,
  // UET Consent Mode: whether ad_storage granted has been signaled
  uetConsentGranted: false,

  // --- SPA navigation ---
  lastTrackedUrl: "",
  debounceTimer: null as ReturnType<typeof setTimeout> | null,
  pollingUrl: "",

  // --- History API originals (captured before wrapping) ---
  originalPushState: history.pushState.bind(history) as History["pushState"],
  originalReplaceState: history.replaceState.bind(history) as History["replaceState"],

  // --- Server-side cookies (first-party proxy mode) ---
  serverCookies: false,
  serverFbp: null as string | null,
  serverFbc: null as string | null,
  serverExternalId: null as string | null,

  /**
   * The device fingerprint, once it has been computed.
   *
   * Null until the config has settled and consent was granted, and null forever
   * if it was not. Events that leave before it resolves simply carry no
   * fingerprint, which is the honest state rather than a value we would have to
   * wait for on the visitor's main thread.
   */
  fingerprint: null as string | null,

  // --- Debug mode (activated by ?tst_debug in URL) ---
  debug: false,

  // --- Facebook SDK state ---
  pushStateRestored: false,
  autoPageViewsSuppressed: false,
  /** The visitor landed on an excluded page, so the ad pixels have not loaded
   *  yet. They load on the first measured page (see releaseExcludedPixels). */
  pixelsHeldForExcludedPage: false,
  /**
   * The shop's own plugin reports the funnel, so our heuristic stands down.
   *
   * Read at bootstrap, not where it is used. The check used to run inside
   * installEcommerceDetection, which fires from the config callback, and by
   * then document.currentScript is null and the fallback picks the LAST script
   * on the page: WooCommerce's, not ours. The opt-out therefore never applied,
   * and one click on add-to-cart produced two AddToCart events.
   */
  ecommerceDisabled: false,
  /** Only WooCommerce needs a recent CMP snapshot for a later paid order. */
  wooCommercePlugin: false,
  /** WordPress chose Pixel Manager as the browser source for Woo events. */
  metaWooBrowserExternal: false,
  /** Pixel Manager can write its own Consent Mode updates without a CMP. */
  pixelManagerPresent: false,
  fbSdkReadyFired: false,
  fbqIsOurs: false,
  /**
   * Conflicts observed on this page, in the order we noticed them.
   *
   * A set in spirit, an array in practice: this ships to browsers we do not
   * choose, and the payload wants a list anyway.
   */
  pixelConflicts: [] as string[],
  /** Of we andermans fbq al in de gaten houden. Zonder deze vlag zou elke
   *  init een laag om de vorige wikkelen. */
  foreignConversionWatchInstalled: false,
  sdkResolved: false,
  sdkResolvedCallback: null as (() => void) | null,
};

/** Log to console only when debug mode is active (?tst_debug in URL). */
export function debugLog(label: string, ...args: unknown[]): void {
  if (!state.debug) return;
  console.log("[TST] " + label + ":", ...args);
}
